import type { ZodType } from "zod";
import z from "zod";

import {
  IsoDateSchema,
  looseUuid,
  type IsoDateUnbranded,
} from "@thalamos/common";

import {
  InstrumentDigestSchema,
  type InstrumentDigestUnbranded,
} from "../assessments/digest.js";
import {
  SubmissionDocumentSchema,
  type SubmissionDocumentUnbranded,
} from "../assessments/document.js";
import {
  PresentationSchema,
  type PresentationUnbranded,
} from "../assessments/presentation.js";
import {
  AssessmentResponseSchema,
  type AssessmentResponseUnbranded,
} from "../assessments/response.js";
import {
  OrganisationIdSchema,
  type OrganisationIdUnbranded,
} from "./organisation.js";
import { PersonIdSchema, type PersonIdUnbranded } from "./person.js";
import { UserIdSchema, type UserIdUnbranded } from "./user.js";

export type AssessmentIdUnbranded = string;
export const AssessmentIdSchema =
  looseUuid().brand<"haven-common:AssessmentId">() satisfies ZodType<AssessmentIdUnbranded>;
/**
 * One commitment to produce a document an instrument accepts, about one young
 * person. Branded apart from `PersonId` because an assessment and the record it
 * is about are both named in the same requests.
 */
export type AssessmentId = z.infer<typeof AssessmentIdSchema>;

export type AssessmentSubmissionIdUnbranded = string;
export const AssessmentSubmissionIdSchema =
  looseUuid().brand<"haven-common:AssessmentSubmissionId">() satisfies ZodType<AssessmentSubmissionIdUnbranded>;
/**
 * One attempt at an assessment. Branded apart from `AssessmentId` because an
 * assessment and one of its attempts are both UUIDs and are named in the same
 * reads.
 */
export type AssessmentSubmissionId = z.infer<
  typeof AssessmentSubmissionIdSchema
>;

export type AssessmentDecisionIdUnbranded = string;
export const AssessmentDecisionIdSchema =
  looseUuid().brand<"haven-common:AssessmentDecisionId">() satisfies ZodType<AssessmentDecisionIdUnbranded>;
/**
 * An approver's decision on one attempt. Branded apart from the attempt it is
 * about, because the two are named in the same reads.
 */
export type AssessmentDecisionId = z.infer<typeof AssessmentDecisionIdSchema>;

/**
 * How far along an assessment is.
 *
 * `open` is nothing submitted yet. A definitive assessment is then `submitted`
 * until an approver decides its latest submission, `returned` until its owner
 * submits again, and `approved` for good. One that is not definitive needs no
 * decision, so submitting it makes it `completed`. The status always says what
 * the submissions and their decisions say; it is kept on the assessment so a
 * write can be made conditional on it.
 *
 * Written out as a union as well as a schema because tsoa follows a type into
 * the API contract but cannot follow `z.infer`.
 *
 * A state nothing writes is a state every reader must handle and none can see,
 * so a member is added when something can reach it.
 *
 * Stored as written, so a member is added and never renamed.
 */
export type AssessmentStatus =
  "open" | "submitted" | "returned" | "approved" | "completed";

export const AssessmentStatusSchema = z.enum([
  "open",
  "submitted",
  "returned",
  "approved",
  "completed",
]) satisfies ZodType<AssessmentStatus>;

/**
 * What an approver decided about a submission.
 *
 * Stored as written, so a member is added and never renamed.
 */
export type AssessmentDecisionOutcome = "approved" | "returned";

export const AssessmentDecisionOutcomeSchema = z.enum([
  "approved",
  "returned",
]) satisfies ZodType<AssessmentDecisionOutcome>;

/**
 * The instrument an assessment is pinned to: what it is called, and what it is.
 *
 * The name is rendered — `sample/core/1.0/r1` — rather than carried as its
 * segments, because that is what a caller passes back and what a URL holds. The
 * digest is what identifies it: two versions can share no digest, so a reader
 * holding one needs nothing else to fetch exactly what this was validated
 * against.
 */
export type PinnedInstrumentUnbranded = {
  name: string;
  digest: InstrumentDigestUnbranded;
};

export const PinnedInstrumentSchema = z.object({
  name: z.string(),
  digest: InstrumentDigestSchema,
}) satisfies ZodType<PinnedInstrumentUnbranded>;

export type SubmissionNumberUnbranded = number;
export const SubmissionNumberSchema = z
  .number()
  .int()
  .positive()
  .brand<"haven-common:SubmissionNumber">() satisfies ZodType<SubmissionNumberUnbranded>;
/**
 * Which attempt at one assessment a submission is, counted from 1 in the order
 * they were made: what a decision and a reopening name the attempt by. Branded
 * so a count, a limit or an offset cannot stand in for one.
 */
export type SubmissionNumber = z.infer<typeof SubmissionNumberSchema>;

/**
 * An approver's decision on one submission: who made it, when, and — for a
 * return — why.
 *
 * Two shapes, so a reader never meets an approval that gives a reason. A
 * return always has one on the record, but its `reason` is `null` for a reader
 * who may not read what the assessment says, who sees only that it was
 * returned: the reason is prose about an assessment and so about a child,
 * which is also why it is here on the record rather than in the trail.
 */
export type AssessmentDecisionUnbranded =
  | {
      id: AssessmentDecisionIdUnbranded;
      outcome: "approved";
      decidedByUserId: UserIdUnbranded;
      decidedAt: Date;
      reason: null;
    }
  | {
      id: AssessmentDecisionIdUnbranded;
      outcome: "returned";
      decidedByUserId: UserIdUnbranded;
      decidedAt: Date;
      reason: string | null;
    };

export const AssessmentDecisionSchema = z.discriminatedUnion("outcome", [
  z.object({
    id: AssessmentDecisionIdSchema,
    outcome: z.literal("approved"),
    decidedByUserId: UserIdSchema,
    decidedAt: z.coerce.date(),
    reason: z.null(),
  }),
  z.object({
    id: AssessmentDecisionIdSchema,
    outcome: z.literal("returned"),
    decidedByUserId: UserIdSchema,
    decidedAt: z.coerce.date(),
    reason: z.string().nullable(),
  }),
]) satisfies ZodType<AssessmentDecisionUnbranded>;

export type AssessmentSubmissionUnbranded = {
  id: AssessmentSubmissionIdUnbranded;
  number: SubmissionNumberUnbranded;
  submittedByUserId: UserIdUnbranded;
  submittedAt: Date;
  /** The calendar day the answers describe the young person as of. */
  assessedOn: IsoDateUnbranded;
  /** The sender's own reference, opaque to Haven. */
  externalReference: string | null;
  responses: AssessmentResponseUnbranded[];
  /**
   * `null` while it waits for one, and always for an assessment that is not
   * definitive.
   */
  decision: AssessmentDecisionUnbranded | null;
};

/**
 * An assessment: about whom, by whom, against which schema, and how far along.
 *
 * Deliberately says nothing about how a document is collected. A form, or any
 * other collector, records what it did in its own tables and points at this;
 * nothing here points back, so a submission that arrived over the API is the
 * ordinary case rather than one with fields left empty.
 */
export type AssessmentUnbranded = {
  id: AssessmentIdUnbranded;
  personId: PersonIdUnbranded;
  /** What the young person's record said when this was started. */
  personVersion: number;
  organisationId: OrganisationIdUnbranded;
  /** The one account that may submit it. */
  ownerUserId: UserIdUnbranded;
  instrument: PinnedInstrumentUnbranded;
  /**
   * Whether its owner held the young person's designated-assessor involvement
   * when it was started, which is what makes a submission of it wait for an
   * approver. Fixed for its life.
   */
  definitive: boolean;
  status: AssessmentStatus;
  startedAt: Date;
  deletion: AssessmentDeletionUnbranded | null;
  submissions: AssessmentSubmissionUnbranded[];
  /**
   * Whether what it says was withheld from whoever asked: its submissions are
   * listed without their answers or a return's reason. Absent is not.
   */
  answersWithheld?: boolean | undefined;
};

export type AssessmentDeletionUnbranded = {
  at: Date;
  byUserId: UserIdUnbranded;
  reason: string;
};

export const AssessmentDeletionSchema = z.object({
  at: z.coerce.date(),
  byUserId: UserIdSchema,
  reason: z.string(),
}) satisfies ZodType<AssessmentDeletionUnbranded>;

/**
 * A deletion: when an assessment was taken out of use, by whom, and why.
 *
 * An instant, an author and a reason together, because any one of the three
 * alone is a deletion nobody can account for. The reason is prose about an
 * assessment and so about a child, which is why it is here on the record rather
 * than in the trail.
 *
 * Reversible, so this is `null` again once somebody undeletes the assessment.
 */
export type AssessmentDeletion = z.infer<typeof AssessmentDeletionSchema>;

export const AssessmentSubmissionSchema = z.object({
  id: AssessmentSubmissionIdSchema,
  number: SubmissionNumberSchema,
  submittedByUserId: UserIdSchema,
  submittedAt: z.coerce.date(),
  assessedOn: IsoDateSchema,
  externalReference: z.string().nullable(),
  responses: z.array(AssessmentResponseSchema),
  decision: AssessmentDecisionSchema.nullable(),
}) satisfies ZodType<AssessmentSubmissionUnbranded>;

/**
 * One attempt, as it was sent, and what an approver decided about it.
 *
 * Immutable once written, and so is its decision. What produced it, if
 * anything did beyond an API call, is recorded by whatever produced it, never
 * here.
 */
export type AssessmentSubmission = z.infer<typeof AssessmentSubmissionSchema>;

/** Branded, so holding one means it was parsed rather than assembled from rows that looked right. */
export const AssessmentSchema = z
  .object({
    id: AssessmentIdSchema,
    personId: PersonIdSchema,
    personVersion: z.number().int(),
    organisationId: OrganisationIdSchema,
    ownerUserId: UserIdSchema,
    instrument: PinnedInstrumentSchema,
    definitive: z.boolean(),
    status: AssessmentStatusSchema,
    startedAt: z.coerce.date(),
    deletion: AssessmentDeletionSchema.nullable(),
    submissions: z.array(AssessmentSubmissionSchema),
    answersWithheld: z.boolean().optional(),
  })
  .brand<"haven-common:Assessment">() satisfies ZodType<AssessmentUnbranded>;

export type Assessment = z.infer<typeof AssessmentSchema>;

/**
 * An assessment as it is stored, where a return always records its reason.
 * `AssessmentSchema` admits a return with none, because that is how one reads
 * to somebody it is withheld from; a stored row is never that, and parsing one
 * with this refuses a row that says otherwise.
 */
export const StoredAssessmentSchema = AssessmentSchema.refine(
  (assessment) =>
    assessment.submissions.every(
      (submission) =>
        submission.decision?.outcome !== "returned" ||
        submission.decision.reason !== null,
    ),
  { message: "A returned submission records why it was returned" },
);

/**
 * Every assessment under way about one young person; finished ones are paged as
 * their history, and deleted ones are listed separately.
 */
export type PersonAssessmentsUnbranded = {
  assessments: AssessmentUnbranded[];
};

export const PersonAssessmentsResponseSchema = z.object({
  assessments: z.array(AssessmentSchema),
}) satisfies ZodType<PersonAssessmentsUnbranded>;

export type PersonAssessments = z.infer<typeof PersonAssessmentsResponseSchema>;

/**
 * Who sent a submission's evidence of what was shown, from how it arrived
 * rather than anything the request said: our own form, or a sender over the
 * core's API that nothing yet identifies.
 *
 * Stored as written, so a member is added and never renamed.
 */
export type SubmissionCollector = "form" | "unidentified";

export const SubmissionCollectorSchema = z.enum([
  "form",
  "unidentified",
]) satisfies ZodType<SubmissionCollector>;

/** What one attempt was recorded as. */
export type SubmissionRecordedUnbranded = {
  submissionId: AssessmentSubmissionIdUnbranded;
  number: SubmissionNumberUnbranded;
};

export const SubmissionRecordedSchema = z.object({
  submissionId: AssessmentSubmissionIdSchema,
  number: SubmissionNumberSchema,
}) satisfies ZodType<SubmissionRecordedUnbranded>;

export type SubmissionRecorded = z.infer<typeof SubmissionRecordedSchema>;

/**
 * What starting an assessment states: which instrument, by the prefix the body
 * is entitled to rather than a full name.
 *
 * A prefix and not a whole name — see `instrumentPrefixes`. The newest
 * published version extending it is what gets pinned.
 */
export type StartAssessmentRequestUnbranded = {
  instrument: string;
};

export const StartAssessmentRequestSchema = z
  .object({
    instrument: z.string().min(1, "assessment.instrument.required"),
  })
  .brand<"haven-common:StartAssessmentRequest">() satisfies ZodType<StartAssessmentRequestUnbranded>;

export type StartAssessmentRequest = z.infer<
  typeof StartAssessmentRequestSchema
>;

/**
 * What submitting states: the document, the day it describes the young person
 * as of, and optionally what the sender calls this exchange and what the
 * respondent was shown.
 *
 * `assessedOn` is a calendar day, `YYYY-MM-DD`, and may be earlier than the day
 * it is sent: answers typed up after a meeting describe the child at the
 * meeting. Only the sender knows which day that was.
 *
 * `presentation` is the sender's attestation of what was shown (see
 * `PresentationUnbranded`), kept beside the submission.
 */
export type SubmitAssessmentRequestUnbranded = {
  document: SubmissionDocumentUnbranded;
  assessedOn: IsoDateUnbranded;
  externalReference?: string;
  presentation?: PresentationUnbranded;
};

export const SubmitAssessmentRequestSchema = z
  .object({
    document: SubmissionDocumentSchema,
    assessedOn: z.iso.date("assessment.assessedOn.invalid").pipe(IsoDateSchema),
    externalReference: z.string().max(255).optional(),
    presentation: PresentationSchema.optional(),
  })
  .brand<"haven-common:SubmitAssessmentRequest">() satisfies ZodType<SubmitAssessmentRequestUnbranded>;

export type SubmitAssessmentRequest = z.infer<
  typeof SubmitAssessmentRequestSchema
>;

/**
 * What deleting an assessment states: why.
 *
 * Required, because a deletion without one is a record nobody can account
 * for — the constraint on the row says the same thing.
 */
export type DeleteAssessmentRequestUnbranded = {
  reason: string;
};

export const DeleteAssessmentRequestSchema = z
  .object({
    reason: z
      .string()
      .trim()
      .min(1, "assessment.deletion.reasonRequired")
      .max(4000, "assessment.deletion.reasonTooLong"),
  })
  .brand<"haven-common:DeleteAssessmentRequest">() satisfies ZodType<DeleteAssessmentRequestUnbranded>;

export type DeleteAssessmentRequest = z.infer<
  typeof DeleteAssessmentRequestSchema
>;

/**
 * What an approver decides about a submission: approve it, or return it saying
 * why.
 *
 * A return carries a reason and an approval does not, and the two are separate
 * shapes rather than one with an optional field, so a parsed approval never
 * carries a reason. The reason is the only way an approver points at what needs
 * another look, which is why a return cannot be made without one.
 */
export type DecideAssessmentRequestUnbranded =
  { outcome: "approved" } | { outcome: "returned"; reason: string };

export const DecideAssessmentRequestSchema = z
  .discriminatedUnion("outcome", [
    z.object({ outcome: z.literal("approved") }),
    z.object({
      outcome: z.literal("returned"),
      reason: z
        .string()
        .trim()
        .min(1, "assessment.decision.reasonRequired")
        .max(4000, "assessment.decision.reasonTooLong"),
    }),
  ])
  .brand<"haven-common:DecideAssessmentRequest">() satisfies ZodType<DecideAssessmentRequestUnbranded>;

export type DecideAssessmentRequest = z.infer<
  typeof DecideAssessmentRequestSchema
>;

/** What one decision was recorded as. */
export type DecisionRecordedUnbranded = {
  decisionId: AssessmentDecisionIdUnbranded;
};

export const DecisionRecordedSchema = z.object({
  decisionId: AssessmentDecisionIdSchema,
}) satisfies ZodType<DecisionRecordedUnbranded>;

export type DecisionRecorded = z.infer<typeof DecisionRecordedSchema>;

export type AssessmentSummaryUnbranded = {
  id: AssessmentIdUnbranded;
  personId: PersonIdUnbranded;
  ownerUserId: UserIdUnbranded;
  instrument: PinnedInstrumentUnbranded;
  definitive: boolean;
  status: AssessmentStatus;
  startedAt: Date;
  submissionCount: number;
};

/**
 * How many of somebody's own assessments one page may carry.
 *
 * A ceiling as well as a default: an account goes on starting assessments for
 * as long as it is used, so a listing with no limit is a copy of everything
 * somebody has ever done.
 */
export const OWN_ASSESSMENTS_PAGE_LIMIT_MAX = 100;
export const OWN_ASSESSMENTS_PAGE_LIMIT_DEFAULT = 25;

export type DeletedAssessmentUnbranded = AssessmentSummaryUnbranded & {
  deletion: AssessmentDeletionUnbranded;
};

export const AssessmentSummarySchema = z.object({
  id: AssessmentIdSchema,
  personId: PersonIdSchema,
  ownerUserId: UserIdSchema,
  instrument: PinnedInstrumentSchema,
  definitive: z.boolean(),
  status: AssessmentStatusSchema,
  startedAt: z.coerce.date(),
  submissionCount: z.number().int(),
}) satisfies ZodType<AssessmentSummaryUnbranded>;

/**
 * One assessment as a listing reports it: what it is, and not what it holds.
 *
 * Responses and the prose beside them are about a child, and reach over her
 * record is what governs who reads them. A listing that cannot ask that
 * question of every row it returns reports this instead; handing over the
 * answers would be the way around the question rather than a summary of it.
 *
 * How many attempts an assessment carried is the most this says about them.
 */
export type AssessmentSummary = z.infer<typeof AssessmentSummarySchema>;

export const DeletedAssessmentSchema = AssessmentSummarySchema.extend({
  deletion: AssessmentDeletionSchema,
}) satisfies ZodType<DeletedAssessmentUnbranded>;

/** One deleted assessment: what it was, and why it went. */
export type DeletedAssessment = z.infer<typeof DeletedAssessmentSchema>;

/**
 * A submitted assessment waiting for somebody's decision, since when, and the
 * names an approver picks it out by.
 *
 * `submittedAt` is when its latest submission arrived, which is when it began
 * waiting; the earlier submissions were each returned. Both names travel with
 * each row: the reader may decide it, so may read its young person, but may
 * not read the assessor's account to look theirs up.
 */
export type AwaitingDecisionUnbranded = AssessmentSummaryUnbranded & {
  submittedAt: Date;
  /** The young person, by the name their record shows. */
  personName: string;
  /** Whose work it is. */
  assessorName: string;
};

export const AwaitingDecisionSchema = AssessmentSummarySchema.extend({
  submittedAt: z.coerce.date(),
  personName: z.string(),
  assessorName: z.string(),
}) satisfies ZodType<AwaitingDecisionUnbranded>;

export type AwaitingDecision = z.infer<typeof AwaitingDecisionSchema>;

/** A page of what waits for somebody's decision, and how much waits in all. */
export type AwaitingDecisionPageUnbranded = {
  results: AwaitingDecisionUnbranded[];
  total: number;
};

export const AwaitingDecisionPageSchema = z.object({
  results: z.array(AwaitingDecisionSchema),
  total: z.number().int(),
}) satisfies ZodType<AwaitingDecisionPageUnbranded>;

export type AwaitingDecisionPage = z.infer<typeof AwaitingDecisionPageSchema>;

/**
 * How many assessments awaiting somebody's decision one page may carry.
 *
 * A ceiling as well as a default: a response nobody bounded is sized by
 * whatever has accumulated.
 */
export const AWAITING_DECISION_PAGE_LIMIT_MAX = 100;
export const AWAITING_DECISION_PAGE_LIMIT_DEFAULT = 25;

/** How an assessment finished: approved, or completed where it needed no approval. */
export type FinishedAssessmentStatus = "approved" | "completed";

export const FinishedAssessmentStatusSchema = z.enum([
  "approved",
  "completed",
]) satisfies ZodType<FinishedAssessmentStatus>;

/**
 * A finished assessment as a young person's history lists it: what it was, how
 * and when it finished, and who approved it.
 *
 * Nothing it says, and none of its submissions: opening one reads the
 * assessment itself, under the rules for reading what it says.
 */
export type FinishedAssessmentUnbranded = Omit<
  AssessmentSummaryUnbranded,
  "status"
> & {
  status: FinishedAssessmentStatus;
  /** When it was approved, or when the submission that completed it was made. */
  finishedAt: Date;
  /** Who approved it; null for one that needed no approval. */
  approvedByUserId: UserIdUnbranded | null;
};

export const FinishedAssessmentSchema = AssessmentSummarySchema.extend({
  status: FinishedAssessmentStatusSchema,
  finishedAt: z.coerce.date(),
  approvedByUserId: UserIdSchema.nullable(),
}) satisfies ZodType<FinishedAssessmentUnbranded>;

export type FinishedAssessment = z.infer<typeof FinishedAssessmentSchema>;

/**
 * A page of one person's finished assessments, how many there are in
 * all, and how many guests each one on the page is shared with through an
 * involvement that has neither ended nor expired, by its id. An assessment
 * shared with no such guest is absent from `guestsSharedWith`.
 */
export type AssessmentHistoryPageUnbranded = {
  results: FinishedAssessmentUnbranded[];
  total: number;
  guestsSharedWith: Record<AssessmentIdUnbranded, number>;
};

export const AssessmentHistoryPageSchema = z.object({
  results: z.array(FinishedAssessmentSchema),
  total: z.number().int(),
  guestsSharedWith: z.record(z.string(), z.number().int().nonnegative()),
}) satisfies ZodType<AssessmentHistoryPageUnbranded>;

export type AssessmentHistoryPage = z.infer<typeof AssessmentHistoryPageSchema>;

/**
 * How many of a young person's finished assessments one page may carry.
 *
 * A ceiling as well as a default: a child's history goes on growing for as
 * long as they are known. A row is a summary, so the ceiling is set high
 * enough for the whole of any real history to be read.
 */
export const ASSESSMENT_HISTORY_PAGE_LIMIT_MAX = 1000;
export const ASSESSMENT_HISTORY_PAGE_LIMIT_DEFAULT = 25;
